import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import "./ClipCard.css";
import ScheduleModal from "../ScheduleModal/ScheduleModal";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";

const ACTION_OPTIONS = [
  { id: "schedule", labelKey: "schedule" },
  { id: "publish", labelKey: "post" },
];

// Apple-style emoji rendering via Twemoji CDN (no npm dependency)
const TWEMOJI_CDN = 'https://cdn.jsdelivr.net/gh/jdecked/twemoji@15.1.0/assets/svg/';
const EMOJI_REGEX = /\p{Extended_Pictographic}(\uFE0F|\u200D\p{Extended_Pictographic})*/gu;

const emojiToCodePoints = (emoji) =>
  [...emoji].map(c => c.codePointAt(0)).filter(cp => cp !== 0xFE0F).map(cp => cp.toString(16)).join('-');

const EmojiText = ({ children, className, style }) => {
  if (!children) return null;
  const text = String(children);
  const parts = [];
  let lastIndex = 0;
  const regex = new RegExp(EMOJI_REGEX.source, EMOJI_REGEX.flags);
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const cp = emojiToCodePoints(match[0]);
    parts.push(
      <img key={match.index} className="twemoji" src={`${TWEMOJI_CDN}${cp}.svg`} alt={match[0]} draggable={false} />
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <span className={className} style={style}>{parts}</span>;
};

const ClipCard = ({ clip, index, clipJobId, userPlan, onUpgrade, vote: voteProp, onVote, hookScore, isBestClip }) => {
  const { t } = useTranslation('clips');
  const isFreeTrial = !userPlan || userPlan === "none";
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoPaused, setVideoPaused] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showPostMenu, setShowPostMenu] = useState(false);
  const [selectedAction, setSelectedAction] = useState("schedule");
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [postingHistory, setPostingHistory] = useState([]);
  const [currentVote, setCurrentVote] = useState(voteProp || null);
  const [voting, setVoting] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [slowConnection, setSlowConnection] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [inView, setInView] = useState(false);

  const hookText = clip.hook || "";

  // Prefer the editor's latest output. Falls back to the original
  // server render for clips the user hasn't edited.
  const videoSrc = clip.editedUrl || clip.url;

  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const postMenuRef = useRef(null);
  const cardRef = useRef(null);
  const lastTimeUpdateRef = useRef(0);
  const waitingTimerRef = useRef(null);
  const slowTimerRef = useRef(null);

  // Buffering detection
  const showSpinnerIfStuck = () => {
    clearTimeout(waitingTimerRef.current);
    waitingTimerRef.current = setTimeout(() => {
      const since = performance.now() - (lastTimeUpdateRef.current || 0);
      if (videoRef.current && !videoRef.current.paused && since > 400) {
        setIsBuffering(true);
      }
    }, 250);
  };

  useEffect(() => {
    return () => { clearTimeout(waitingTimerRef.current); clearTimeout(slowTimerRef.current); };
  }, []);

  // Slow connection detection: if buffering lasts >3s, show label
  useEffect(() => {
    if (isBuffering) {
      slowTimerRef.current = setTimeout(() => setSlowConnection(true), 3000);
    } else {
      clearTimeout(slowTimerRef.current);
      setSlowConnection(false);
    }
    return () => clearTimeout(slowTimerRef.current);
  }, [isBuffering]);

  // Lazy loading
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); io.disconnect(); } },
      { rootMargin: '200px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Sync vote from parent
  useEffect(() => {
    setCurrentVote(voteProp || null);
  }, [voteProp]);

  const handleVote = async (voteType) => {
    if (voting) return;
    setVoting(true);
    const prevVote = currentVote;
    const newVote = currentVote === voteType ? null : voteType;
    setCurrentVote(newVote);
    try {
      if (newVote) {
        await axiosInstance.post("/clips/feedback", { jobId: clipJobId, clipIndex: index, vote: newVote });
      } else {
        await axiosInstance.delete("/clips/feedback", { data: { jobId: clipJobId, clipIndex: index } });
      }
      onVote?.(index, newVote);
    } catch (err) {
      console.error("Vote failed:", err);
      setCurrentVote(prevVote);
    } finally {
      setVoting(false);
    }
  };

  // Fetch posting history
  useEffect(() => {
    const fetchPostingHistory = async () => {
      if (!clip.url) return;
      try {
        const res = await axiosInstance.get("/scheduled-posts/by-clip", { params: { clipUrl: clip.url } });
        setPostingHistory(res.data.history || []);
      } catch (err) {
        console.log("Could not fetch posting history:", err);
      }
    };
    fetchPostingHistory();
  }, [clip.url]);

  const getPostedPlatforms = () => {
    const posted = [];
    for (const post of postingHistory) {
      if (post.status === "completed" || post.status === "partial") {
        for (const platform of post.platforms) {
          if (platform.status === "posted" && !posted.find(p => p.name === platform.name)) {
            posted.push({ name: platform.name, postUrl: platform.postUrl, postedAt: platform.postedAt });
          }
        }
      }
    }
    return posted;
  };
  const postedPlatforms = getPostedPlatforms();

  // Close post menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (postMenuRef.current && !postMenuRef.current.contains(e.target)) setShowPostMenu(false);
    };
    if (showPostMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPostMenu]);

  // Thumbnail strategy:
  //  1. PREFER server-generated thumbnailUrl from the worker (one cheap JPEG
  //     fetch instead of pulling the entire MP4 just to canvas a frame).
  //     New clips get this; old clips don't have it set.
  //  2. FALLBACK: canvas-extract a frame from the loaded video element
  //     (legacy path — kept for clips rendered before the per-clip thumbnail
  //     change shipped on the worker).
  useEffect(() => {
    if (clip.thumbnailUrl) {
      setThumbnailUrl(clip.thumbnailUrl);
      return;             // skip the expensive video-download/canvas path
    }
    const video = previewRef.current;
    if (!video) return;
    const generateThumbnail = () => {
      try {
        if (video.videoWidth === 0 || video.videoHeight === 0) return;
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        if (dataUrl && dataUrl !== 'data:,') setThumbnailUrl(dataUrl);
      } catch (err) {
        console.log('Thumbnail generation skipped (CORS)');
      }
    };
    const handleLoadedMetadata = () => { video.currentTime = 0.1; };
    const handleSeeked = () => { generateThumbnail(); };
    const handleLoadedData = () => { if (!thumbnailUrl) generateThumbnail(); };
    const handleCanPlay = () => { if (video.currentTime === 0 && video.readyState >= 2) video.currentTime = 0.1; };
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('canplay', handleCanPlay);
    video.load();
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [clip.url, clip.thumbnailUrl, thumbnailUrl]);

  const handleRetry = (e) => {
    e.stopPropagation();
    setVideoError(false);
    setIsBuffering(true);
    const v = videoRef.current;
    if (v) { v.load(); v.play().catch(() => {}); }
  };

  const handlePlayClick = (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    setIsPlaying(true);
    setVideoPaused(false);
    setIsBuffering(true);
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.play().catch(err => { console.error('Play failed:', err); setIsBuffering(false); });
      }
    }, 50);
  };

  const handleVideoClick = (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(err => console.error('Play failed:', err));
      setVideoPaused(false);
    } else {
      video.pause();
      setVideoPaused(true);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0:30";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // ===== Download — always use pre-burned server clip =====
  const handleDownload = async (e) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    setDownloadProgress(0.2);
    try {
      const safeFilename = (hookText || `clip-${index + 1}`).replace(/[^a-zA-Z0-9-_ ]/g, "_") + ".mp4";
      const response = await axiosInstance.get("/clips/download-url", {
        params: { url: clip.editedUrl || clip.url, filename: hookText || `clip-${index + 1}` },
      });
      const link = document.createElement("a");
      link.href = response.data.url;
      link.download = safeFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadProgress(1);
    } catch (err) {
      console.error("[Download] FAILED:", err.message);
      toast.error(`Download failed: ${err.message || "Unknown error"}`);
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
    }
  };

  return (
    <div className="clip-card" ref={cardRef}>
      {/* Video Container */}
      <div className="clip-card-media">
        {isPlaying ? (
          <div className="clip-card-video-container">
            <div className="clip-card-video-inner">
              <video
                ref={videoRef}
                src={videoSrc}
                autoPlay
                loop
                playsInline
                className="clip-card-video"
                controlsList="nodownload nofullscreen noremoteplayback"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                onClick={handleVideoClick}
                onPlay={() => setVideoPaused(false)}
                onPause={() => { setVideoPaused(true); setIsBuffering(false); }}
                onPlaying={() => setIsBuffering(false)}
                onCanPlay={() => setIsBuffering(false)}
                onWaiting={showSpinnerIfStuck}
                onStalled={showSpinnerIfStuck}
                onTimeUpdate={() => {
                  lastTimeUpdateRef.current = performance.now();
                  if (isBuffering && videoRef.current?.readyState >= 3) setIsBuffering(false);
                }}
                onError={() => { setIsBuffering(false); setVideoError(true); }}
              />
            </div>
            {isBuffering && !videoError && (
              <div className="clip-card-buffering">
                <div className="clip-card-buffering-spinner" />
                {slowConnection && (
                  <span className="clip-card-buffering-label">{t('slowConnection')}</span>
                )}
              </div>
            )}
            {videoError && (
              <div className="clip-card-error-overlay" onClick={(e) => e.stopPropagation()}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span className="clip-card-error-label">{t('couldntLoad')}</span>
                <button className="clip-card-retry-btn" onClick={handleRetry}>{t('tapToRetry')}</button>
              </div>
            )}
            <div
              className="clip-card-controls-overlay"
              style={{ opacity: videoPaused ? 1 : 0, pointerEvents: videoPaused ? 'auto' : 'none' }}
            >
              <button className="clip-card-control-btn clip-card-play-pause-btn" onClick={handleVideoClick} aria-label="Play">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="clip-card-thumbnail" onClick={handlePlayClick}>
            <div className="clip-card-video-inner">
              {inView ? (
                <video
                  ref={previewRef}
                  src={videoSrc}
                  muted
                  playsInline
                  /* When we already have a server-rendered thumbnail, skip
                     preloading the MP4 entirely — saves MBs per card on
                     mobile. Falls back to "metadata" preload only when we
                     need to canvas-extract a frame from the video. */
                  preload={clip.thumbnailUrl ? "none" : "metadata"}
                  poster={thumbnailUrl || undefined}
                  className="clip-card-preview"
                  style={{ backgroundColor: '#18181b' }}
                />
              ) : (
                <div className="clip-card-preview" style={{ backgroundColor: '#18181b' }} />
              )}
            </div>
            <div className="clip-card-play-overlay">
              <div className="clip-card-play-btn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              </div>
            </div>
            <div className="clip-card-duration">{formatDuration(clip.duration)}</div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="clip-card-content">
        {/* Hook text */}
        <div className="clip-card-title-row">
          <h3 className="clip-card-title">
            <EmojiText>{hookText || t('clipDefaultTitle', { number: index + 1 })}</EmojiText>
          </h3>
        </div>

        {/* Hook Score */}
        {hookScore !== undefined && (
          <div className={`clip-card-hook-score${isBestClip ? ' best' : ''}`}>
            {isBestClip && <span className="clip-card-best-label">Best</span>}
            <span className="clip-card-score-value">{hookScore}</span>
            <span className="clip-card-score-label">Hook Score</span>
          </div>
        )}

        {/* Feedback buttons */}
        <div className="clip-card-feedback">
          <button
            className={`clip-card-vote-btn ${currentVote === "up" ? "active up" : ""}`}
            onClick={(e) => { e.stopPropagation(); handleVote("up"); }}
            disabled={voting}
            title={t('goodClip')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill={currentVote === "up" ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
            </svg>
          </button>
          <button
            className={`clip-card-vote-btn ${currentVote === "down" ? "active down" : ""}`}
            onClick={(e) => { e.stopPropagation(); handleVote("down"); }}
            disabled={voting}
            title={t('badClip')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill={currentVote === "down" ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
              <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10zM17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
            </svg>
          </button>
        </div>

        {/* Posted platforms badges */}
        {postedPlatforms.length > 0 && (
          <div className="clip-card-posted-badges">
            {postedPlatforms.map((platform) => (
              <a
                key={platform.name}
                href={platform.postUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`clip-card-posted-badge ${platform.name}`}
                title={`Posted to ${platform.name}${platform.postedAt ? ` on ${new Date(platform.postedAt).toLocaleDateString()}` : ''}`}
                onClick={(e) => e.stopPropagation()}
              >
                {platform.name === "tiktok" && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
                )}
                {platform.name === "youtube" && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                )}
                {platform.name === "instagram" && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
                )}
                <span>{platform.name}</span>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
              </a>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="clip-card-actions">
          <div className="clip-card-post-wrapper" ref={postMenuRef}>
            <button
              onClick={(e) => { e.stopPropagation(); setShowScheduleModal(true); }}
              className="clip-card-post-btn"
              title={t(selectedAction === "publish" ? "post" : "schedule")}
            >
              {t(ACTION_OPTIONS.find(a => a.id === selectedAction)?.labelKey || "post")}
            </button>
            <button
              className="clip-card-post-dropdown"
              onClick={(e) => { e.stopPropagation(); if (isFreeTrial) { onUpgrade?.(); return; } setShowPostMenu(!showPostMenu); }}
              title={t('moreOptions')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
            {showPostMenu && (
              <div className="clip-card-post-menu">
                {ACTION_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    className={`clip-card-post-menu-item ${selectedAction === option.id ? "selected" : ""}`}
                    onClick={(e) => { e.stopPropagation(); setSelectedAction(option.id); setShowPostMenu(false); }}
                  >
                    <span>{t(option.labelKey)}</span>
                    {selectedAction === option.id && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            className={`clip-card-download-btn ${downloading ? 'downloading' : ''}`}
            onClick={handleDownload}
            disabled={downloading}
            title={t('downloadClip')}
          >
            {downloading ? (
              <div className="clip-card-download-progress">
                <svg className="clip-card-download-ring" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15" fill="none" stroke="#ff6a00" strokeWidth="3" strokeLinecap="round" strokeDasharray={`${(downloadProgress || 0) * 94.25} 94.25`} transform="rotate(-90 18 18)" />
                </svg>
                <span className="clip-card-download-pct">{Math.round((downloadProgress || 0) * 100)}</span>
              </div>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            )}
          </button>

          {/* Edit button hidden — pre-burned clips only */}
        </div>
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && createPortal(
        <ScheduleModal
          clip={clip}
          clipJobId={clipJobId}
          clipIndex={index}
          onClose={() => setShowScheduleModal(false)}
          onScheduled={() => setShowScheduleModal(false)}
          initialAction={selectedAction}
        />,
        document.body
      )}
    </div>
  );
};

export default ClipCard;
